import { FC, useEffect, useRef, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import styled from 'styled-components';
import ModalDialog from '../../../shared/components/modal/ModalDialog';
import { flex } from '../../../styles/utilities/flex';
import LCFormFieldInputTextarea from '../shared/LCFormFieldInputTextarea';
import { LCFormField } from '../shared/LCFormField';
import { LCFormFieldInputText } from '../shared/LCFormFieldInputText';
import { MATERIAL_ICON_UPLOAD } from '../../../shared/common/Button/Icon';
import LCPerformerCountryDropdown from './LCPerformerCountryDropdown';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { PerformerOut } from '../../../store/api';
import { useCountries } from '../api/useCountries';
import { useTranslation } from 'react-i18next';

const StWrapperFlexRowSpaceBetween = styled.div`
    ${flex('row')};
    justify-content: space-between;
`;

const StPortraitContainer = styled.div`
    width: 22%;
`;

const StFormContainer = styled.div`
    width: 70%;
`;

const StPortraitImage = styled.img<{ authorHasPhoto: boolean }>`
    display: ${props => (props.authorHasPhoto ? 'initial' : 'none')};
    max-width: 100%;
    margin: 10px 0;
`;

const StPortraitLabel = styled.span`
    font-weight: 350;
    font-size: 18px;
    color: #7a7a7a !important;
    width: 30%;
    margin: 1px 0;
`;

const StFormHeader = styled.h3`
    font-weight: 350;
    color: #7a7a7a !important;
    font-size: 18px;
    margin-top: 5px;
`;

const StPairRow = styled.div`
    ${flex('row')};
    justify-content: space-between;
    flex-grow: 1;
`;

const StSpacerBetweenRowItems = styled.span`
    display: block;
    margin-left: 1.75em;
    margin-right: 1.75em;
`;

const StSpacerBetweenActionButtons = styled.span`
    display: block;
    margin-left: 0.75em;
    margin-right: 0.75em;
`;

const StSpacerBetweenData = styled.span`
    display: block;
    padding-bottom: 2.5em;
`;

interface Props {
    performer?: PerformerOut;
    show: boolean;
    onAbort: () => void;
    onSuccess: (id, id2) => void;
    isNew: boolean;
    onDelete?: () => void;
}
export const LCPerformerPopup: FC<Props> = props => {
    const { t } = useTranslation();
    const { onAbort = () => {}, onSuccess = () => {}, onDelete = () => {} } = props;
    const [performer, setPerformer] = useState<PerformerOut & { photoDeletion?: boolean; photoUrlObject?: object }>();
    const [selectedCountryName, setSelectedCountryName] = useState<{ key: string | undefined; value: string }>({
        key: '',
        value: ''
    });
    const authorPhotoUpload = useRef<HTMLInputElement>(null);
    const authorPhotoImg = useRef<HTMLImageElement>(null);
    const { data: countriesList } = useCountries({ enabled: props.show });
    const countries = countriesList?.map(country => {
        return {
            key: country.code,
            value: t(`countries:countries.${country.code}`, { defaultValue: country.name })
        };
    });
    const countryName = countries?.find(item => item.key === performer?.country);

    useEffect(() => {
        if (props.show && props.performer) {
            setPerformer(prevPerformer => ({ ...prevPerformer, ...props.performer }));
            setSelectedCountryName({
                key: props.performer.country,
                value: t(`countries:countries.${props.performer.country}`, {
                    defaultValue: t('yourLocalContent.yourAuthors.selectCountry')
                })
            });
        }
    }, [props.show, props.performer, t]);

    const updateValueInState = (key: string, value: string) => {
        setPerformer({ ...performer, [key]: value });
    };

    function abort() {
        onAbort();
        setPerformer(undefined);
        setSelectedCountryName({ key: '', value: t('yourLocalContent.yourAuthors.selectCountry') });
    }

    function handleAuthorPhotoChange(event) {
        const files = event.target.files;
        const objectUrl = window.URL.createObjectURL(files[0]);
        if (authorPhotoImg.current) {
            authorPhotoImg.current.src = objectUrl;
        }

        if (authorPhotoImg.current) {
            authorPhotoImg.current.onload = () => window.URL.revokeObjectURL(authorPhotoImg?.current?.src ?? '');
        }

        setPerformer({ ...performer, photoDeletion: false, photoUrlObject: files[0], photoUrl: objectUrl });
    }

    function removeAuthorPhoto() {
        if (performer?.photoUrl) {
            setPerformer({ ...performer, photoDeletion: true, photoUrl: '', photoUrlObject: {} });
        }

        if (authorPhotoImg.current) {
            authorPhotoImg.current.src = '';
        }
    }

    function save() {
        const authorFormData = { ...performer, country: selectedCountryName.key };
        onSuccess({ ...authorFormData }, performer?.photoUrlObject);
        setPerformer(undefined);
    }

    function deletePerformer() {
        onDelete();
        setPerformer(undefined);
    }

    const authorHasPhoto = !(isNil(performer?.photoUrl) || isEmpty(performer?.photoUrl));

    return (
        <ModalDialog
            show={props.show}
            titleClassName="black-title"
            onCloseButtonClick={onAbort}
            title={
                props.isNew
                    ? t('yourLocalContent.yourAuthors.createNewAuthor')
                    : t('yourLocalContent.yourAuthors.editAuthor')
            }
            wrapClassName="lc-create-author-popup"
        >
            <StWrapperFlexRowSpaceBetween>
                <a data-displayname="LCPerformerPopup" hidden />
                <StPortraitContainer>
                    <StWrapperFlexRowSpaceBetween>
                        <StPortraitLabel>{t('yourLocalContent.yourAuthors.photo')}</StPortraitLabel>
                        <Button
                            onClick={() => authorPhotoUpload?.current?.click()}
                            size={ButtonSize.LARGE}
                            theme={ButtonTheme.NEUTRAL}
                            text={t('yourLocalContent.yourAuthors.uploadPhoto')}
                            icon={MATERIAL_ICON_UPLOAD}
                        />

                        <input
                            ref={authorPhotoUpload}
                            type="file"
                            id="authorPhoto"
                            name="authorPhoto"
                            style={{ display: 'none' }}
                            accept="image/*"
                            onChange={handleAuthorPhotoChange}
                        />
                    </StWrapperFlexRowSpaceBetween>
                    <StPortraitImage
                        src={performer?.photoUrl}
                        ref={authorPhotoImg}
                        authorHasPhoto={authorHasPhoto}
                        alt={performer?.lastName}
                    />
                    {authorHasPhoto ? (
                        <Button
                            fullWidth={true}
                            onClick={removeAuthorPhoto}
                            size={ButtonSize.LARGE}
                            theme={ButtonTheme.NEUTRAL}
                            text={t('yourLocalContent.yourAuthors.removePhoto')}
                        />
                    ) : null}
                </StPortraitContainer>
                <StFormContainer>
                    <StFormHeader>{t('yourLocalContent.yourAuthors.details')}</StFormHeader>
                    <StPairRow>
                        <LCFormField
                            title={t('yourLocalContent.yourAuthors.firstName')}
                            tooltipText={t('yourLocalContent.yourAuthors.tooltips.firstName')}
                            mandatory={false}
                            isEdit={true}
                            editComponent={
                                <LCFormFieldInputText
                                    onValueChange={value => updateValueInState('firstName', value)}
                                    value={performer?.firstName}
                                    maxLength={240}
                                />
                            }
                        />
                        <StSpacerBetweenRowItems />
                        <LCFormField
                            title={t('yourLocalContent.yourAuthors.lastName')}
                            tooltipText={t('yourLocalContent.yourAuthors.tooltips.lastName')}
                            mandatory={true}
                            isEdit={true}
                            editComponent={
                                <LCFormFieldInputText
                                    onValueChange={value => updateValueInState('lastName', value)}
                                    value={performer?.lastName}
                                    maxLength={240}
                                />
                            }
                        />
                    </StPairRow>
                    <StSpacerBetweenData />
                    <LCFormField
                        title={t('yourLocalContent.yourAuthors.marcAuthorisedName')}
                        tooltipText={t('yourLocalContent.yourAuthors.tooltips.marcAuthorisedName')}
                        mandatory={false}
                        isEdit={true}
                        editComponent={
                            <LCFormFieldInputText
                                onValueChange={value => updateValueInState('marcAuthorisedName', value)}
                                value={performer?.marcAuthorisedName}
                            />
                        }
                    />
                    <StSpacerBetweenData />
                    <StPairRow>
                        <LCFormField
                            title={t('yourLocalContent.yourAuthors.country')}
                            tooltipText={t('yourLocalContent.yourAuthors.tooltips.country')}
                            mandatory={true}
                            isEdit={true}
                            editComponent={
                                <LCPerformerCountryDropdown
                                    onValueChange={value => setSelectedCountryName(value)}
                                    value={{
                                        current: selectedCountryName.value ?? countryName?.value,
                                        all: countries ?? []
                                    }}
                                />
                            }
                        />
                        <StSpacerBetweenRowItems />
                        <LCFormField
                            title={t('yourLocalContent.yourAuthors.website')}
                            tooltipText={t('yourLocalContent.yourAuthors.tooltips.website')}
                            mandatory={false}
                            isEdit={true}
                            editComponent={
                                <LCFormFieldInputText
                                    onValueChange={value => updateValueInState('website', value)}
                                    value={performer?.website}
                                    maxLength={240}
                                />
                            }
                        />
                    </StPairRow>
                    <StSpacerBetweenData />
                    <LCFormField
                        title={t('yourLocalContent.yourAuthors.biography')}
                        tooltipText={t('yourLocalContent.yourAuthors.tooltips.biography')}
                        mandatory={true}
                        isEdit={true}
                        editComponent={
                            <LCFormFieldInputTextarea
                                onValueChange={value => updateValueInState('biography', value)}
                                value={performer?.biography}
                                maxLength={3500}
                            />
                        }
                    />

                    <StSpacerBetweenData />
                    <StWrapperFlexRowSpaceBetween>
                        {!props.isNew ? (
                            <Button
                                onClick={deletePerformer}
                                size={ButtonSize.LARGE}
                                theme={ButtonTheme.NEUTRAL}
                                text={t('yourLocalContent.yourAuthors.deleteAuthor')}
                                className="delete-author-button"
                            />
                        ) : null}
                        <StWrapperFlexRowSpaceBetween>
                            <Button
                                onClick={abort}
                                size={ButtonSize.LARGE}
                                theme={ButtonTheme.NEUTRAL}
                                text={t('cancel')}
                                className="abort-button"
                            />
                            <StSpacerBetweenActionButtons />
                            <Button
                                onClick={save}
                                size={ButtonSize.LARGE}
                                text={props.isNew ? t('yourLocalContent.yourAuthors.createAuthor') : t('save')}
                                className="save-button"
                            />
                        </StWrapperFlexRowSpaceBetween>
                    </StWrapperFlexRowSpaceBetween>
                </StFormContainer>
            </StWrapperFlexRowSpaceBetween>
        </ModalDialog>
    );
};
